import { Box, Typography } from '@mui/material';
import parse, { DOMNode, Element, domToReact } from 'html-react-parser';

import SenderHeader from '../common/SenderHeader';
import AttachmentsAccordion from '../common/AttachmentsAccordion';

import useUser from 'services/help-center/queries/useUser';
import { Comment } from 'services/help-center/queries/useTicketComments/useTicketComments.interface';
import useZDUser from 'services/help-center/queries/useZDUser';
import sanitizeHtml from 'modules/common/utils/sanitizeHtml';

interface MessageProps {
    comment: Comment;
}

// Exported so tests exercise the real link handling rather than a copy that can drift.
export const parseOptions = {
    replace: (domNode: DOMNode) => {
        if (domNode instanceof Element && domNode.name === 'a' && domNode.attribs) {
            return (
                <a href={domNode.attribs.href} target="_blank" rel="noopener noreferrer">
                    {domToReact(domNode.children, parseOptions)}
                </a>
            );
        }
        return domNode;
    },
};

export default function Message({ comment }: MessageProps) {
    const { data: zdUser } = useZDUser();
    const isCommentFromZDUser = zdUser?.id === comment.author_id;

    const { data: zdSender, isPending } = useUser(comment.author_id, {
        enabled: !isCommentFromZDUser,
    });

    const sender = !isCommentFromZDUser && isPending ? null : zdSender || (zdUser as typeof zdSender);

    return (
        <Box sx={{ paddingY: 2.5, paddingX: 3 }}>
            <SenderHeader sender={sender ?? { name: 'Unknown' }} timestamp={comment.created_at} />
            <Box sx={{ pl: 4 }}>
                <Typography
                    component="span"
                    variant="body2"
                    fontWeight={400}
                    sx={{
                        '& .collapse-signature, & .signature': { display: 'none' },
                        '& .zd-comment': { overflowY: 'auto' },
                    }}>
                    {parse(sanitizeHtml(comment.html_body ?? ''), parseOptions)}
                </Typography>
                {comment.attachments?.length > 0 && <AttachmentsAccordion attachments={comment.attachments} />}
            </Box>
        </Box>
    );
}
